import React, { Component } from "react";
import { UserLogin, UserData, validate2fa, validatePhoneLoginOtp, resendPhoneLoginOtp } from "../../api/index";
import Button from "react-bootstrap/Button";
import { Col, Row, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import "./Login.scss";

export default class Login extends Component {
    constructor(props) {
        super(props);
        this.state = {
            message: "",
            password: "",
            email: "",
            formError: [],
            user: {},
            passwordVisible: false,
            requires2fa: false,
            twoFactorToken: null,
            twoFactorCode: "",
            requiresPhoneOtp: false,
            phoneOtpToken: null,
            phoneMasked: "",
            resendingPhoneOtp: false,
        };
    }

    componentDidMount() {
        UserData()
            .then((response) => {
                if (response.id) {
                    this.props.history.push("/applications");
                }
            })
            .catch((err) => {
                this.setState({
                    user: null,
                });
            });
    }

    validateForm = () => {
        return this.state.email.length > 0 && this.state.password.length > 0;
    };

    handleSubmit = (event) => {
        event.preventDefault();
        const payload = {
            email: this.state.email,
            password: this.state.password,
        };
        UserLogin(payload)
            .then((response) => {
                if (response.requires_2fa) {
                    this.setState({
                        requires2fa: true,
                        twoFactorToken: response.two_factor_token,
                        message: "",
                    });
                    return;
                }
                // Verified cell number: a code has just been texted, and the
                // session is not created until it comes back correct.
                if (response.requires_phone_otp) {
                    this.setState({
                        requiresPhoneOtp: true,
                        phoneOtpToken: response.phone_otp_token,
                        phoneMasked: response.phone_masked || "",
                        twoFactorCode: "",
                        message: "",
                    });
                    return;
                }
                this.props.refreshLogin();
                this.props.history.push("/applications");
            })
            .catch((error) => {
                this.setState({ formError: error.data.errors });
                this.setState({ message: error.data.message });
            });
    };

    // Shared by both challenges — the SMS code and the authenticator code are
    // entered in the same box, only the endpoint differs.
    handle2faSubmit = (event) => {
        event.preventDefault();
        const request = this.state.requiresPhoneOtp
            ? validatePhoneLoginOtp({
                  phone_otp_token: this.state.phoneOtpToken,
                  code: this.state.twoFactorCode,
              })
            : validate2fa({
                  two_factor_token: this.state.twoFactorToken,
                  code: this.state.twoFactorCode,
              });
        request
            .then((response) => {
                if (response.success) {
                    this.props.refreshLogin();
                    this.props.history.push("/applications");
                } else {
                    this.setState({ message: response.message });
                }
            })
            .catch((error) => {
                this.setState({ message: error?.data?.message || "Invalid verification code" });
            });
    };

    // Only the SMS challenge has this — an authenticator app makes its own codes.
    // Without it a burnt or expired code strands the user on this screen, since the
    // record is deleted after five wrong tries and there is nothing left to enter.
    handleResendPhoneOtp = (event) => {
        event.preventDefault();
        if (this.state.resendingPhoneOtp) {
            return;
        }
        this.setState({ resendingPhoneOtp: true, message: "" });
        resendPhoneLoginOtp({ phone_otp_token: this.state.phoneOtpToken })
            .then((response) => {
                this.setState({
                    // The token is reissued with the code, so the pending login does
                    // not expire before the code it is waiting for.
                    phoneOtpToken: response.phone_otp_token || this.state.phoneOtpToken,
                    phoneMasked: response.phone_masked || this.state.phoneMasked,
                    twoFactorCode: "",
                    message: response.message || "We sent a new code.",
                });
            })
            .catch((error) => {
                if (error?.data?.restart) {
                    this.setState({ requiresPhoneOtp: false, twoFactorCode: "" });
                }
                this.setState({ message: error?.data?.message || "We could not send a new code." });
            })
            .finally(() => this.setState({ resendingPhoneOtp: false }));
    };

    handleChange = (event) => {
        const input = event.target;
        const value = input.type === "checkbox" ? input.checked : input.value;
        this.setState({ [input.name]: value });
    };

    togglePasswordVisibility = () => {
        this.setState({ passwordVisible: !this.state.passwordVisible });
    };

    render() {
        const { email, password, message, formError, user, passwordVisible, requires2fa, twoFactorCode, requiresPhoneOtp, phoneMasked, resendingPhoneOtp } =
            this.state;
        return (
            <div>
                <div className="Login-page">
                    <div className="Login-header">
                        <h1>STRATUS</h1>
                    </div>
                    {requires2fa || requiresPhoneOtp ? (
                        <Form onSubmit={this.handle2faSubmit}>
                            <h2>{requiresPhoneOtp ? "Verify It's You" : "Two-Factor Authentication"}</h2>
                            <p style={{ textAlign: "center", marginBottom: "20px", fontSize: "14px", color: "#666" }}>
                                {requiresPhoneOtp
                                    ? `Enter the 6-digit code we texted to ${phoneMasked || "your phone"}`
                                    : "Enter the 6-digit code from your authenticator app or a recovery code"}
                            </p>
                            <div className="floating-form">
                                <Form.Group size="lg" controlId="twoFactorCode" className="position-relative">
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter code"
                                        value={twoFactorCode}
                                        onChange={(e) => this.setState({ twoFactorCode: e.target.value, message: "" })}
                                        maxLength={requiresPhoneOtp ? 6 : 16}
                                        autoFocus
                                    />
                                    <Form.Label className="floating-label">Verification Code</Form.Label>
                                </Form.Group>
                            </div>
                            <Form.Control.Feedback type="invalid" className="text-capitalize">
                                {message}
                            </Form.Control.Feedback>
                            <Button block size="lg" type="submit" disabled={!twoFactorCode}>
                                Verify →
                            </Button>
                            <div className="forgetpassword">
                                {requiresPhoneOtp && (
                                    <a
                                        href="#"
                                        onClick={this.handleResendPhoneOtp}
                                        style={{ marginRight: "16px" }}
                                    >
                                        {resendingPhoneOtp ? "Sending..." : "Resend code"}
                                    </a>
                                )}
                                <a href="#" onClick={(e) => { e.preventDefault(); this.setState({ requires2fa: false, requiresPhoneOtp: false, twoFactorCode: "", message: "" }); }}>
                                    Back to Sign In
                                </a>
                            </div>
                        </Form>
                    ) : user == null ? (
                        <Form onSubmit={this.handleSubmit}>
                            <h2>STRATUS | Sign In</h2>
                            <div className="floating-form">
                                <Form.Group
                                    size="lg"
                                    controlId="email"
                                    className="position-relative"
                                >
                                    <Form.Control
                                        placeholder="Email"
                                        type="email"
                                        value={email}
                                        onChange={(e) =>
                                            this.setState({
                                                email: e.target.value,
                                                message: "",
                                            })
                                        }
                                    />
                                    <Form.Label className="floating-label">
                                        Email
                                    </Form.Label>
                                    <Form.Control.Feedback
                                        type="invalid"
                                        className="text-capitalize"
                                    >
                                        {formError && formError.email
                                            ? formError.email
                                            : null}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="floating-form">
                                <Form.Group
                                    size="lg"
                                    controlId="password"
                                    className="position-relative"
                                >
                                    <Form.Control
                                        type={
                                            passwordVisible
                                                ? "text"
                                                : "password"
                                        }
                                        placeholder="Password"
                                        value={password}
                                        onChange={(e) =>
                                            this.setState({
                                                password: e.target.value,
                                                message: "",
                                            })
                                        }
                                    />
                                    <Form.Label className="floating-label">
                                        Password
                                    </Form.Label>
                                    <div className="eye-icon">
                                        <FontAwesomeIcon
                                            icon={
                                                passwordVisible
                                                    ? faEyeSlash
                                                    : faEye
                                            }
                                            className="password-icon"
                                            onClick={
                                                this.togglePasswordVisibility
                                            }
                                        />
                                    </div>
                                    <Form.Control.Feedback
                                        type="invalid"
                                        className="text-capitalize"
                                    >
                                        {formError && formError.password
                                            ? formError.password
                                            : null}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <Form.Control.Feedback
                                type="invalid"
                                className="text-capitalize"
                            >
                                {message}
                            </Form.Control.Feedback>
                            <Button
                                block
                                size="lg"
                                type="submit"
                                onClick={this.handleSubmit}
                                disabled={!this.validateForm()}
                            >
                                Sign In →
                            </Button>
                            <div className="forgetpassword">
                                <Link to="/forgotPassword">
                                    Forget Password?{" "}
                                </Link>
                            </div>
                        </Form>
                    ) : (
                        ""
                    )}

                    <div className="copy-right">
                        <p>Copyright © PaymentCloud. All Rights Reserved.</p>
                    </div>
                </div>
                <div className="background-div">
                    <img
                        src="/images/dot_bg.png"
                        alt="background"
                        className="background"
                    />
                </div>
            </div>
        );
    }
}
